import type { SortFieldInfo } from "@/components/data/getSortedItems.ts";
import type { SidebarListId } from "@/components/sidebar/SidebarList.tsx";
import { atomWithLocalStorage } from "@/utils/jotai/atomStorage.ts";
import { withImmer } from "@/utils/jotai/jotaiImmer.ts";
import { safeParseAndLog } from "@/utils/safeParseAndLog.ts";
import { getOwn } from "@warrenio/utils/collections/getOwnProperty";
import { atom, useStore } from "jotai";
import { useState } from "react";
import { z } from "zod";

//#region Types & schemas
const sortItemSchema = z
    .object({
        fieldId: z.string().optional(),
        asc: z.boolean().optional(),
    })
    .passthrough()
    // Invalid values are discarded
    .catch({});

const sortSchema = z.record(z.string(), sortItemSchema);
type ListSorts = z.infer<typeof sortSchema>;
//#endregion

//#region Atoms
const defaultSorts = {};

const sortStorageAtom = atomWithLocalStorage("listSortData", defaultSorts);
const sortAtom = withImmer(
    atom(
        (get) => safeParseAndLog(sortSchema, get(sortStorageAtom), defaultSorts),
        (_get, set, value: ListSorts) => set(sortStorageAtom, value),
    ),
);
//#endregion

function getValidSortId(sortFields: SortFieldInfo[], fieldId: string | undefined): string {
    return fieldId != null && sortFields.some((sortField) => sortField.id === fieldId) ? fieldId : sortFields[0].id;
}

export function useStoredSort(
    listId: SidebarListId,
    sortFields: SortFieldInfo[],
): {
    sortId: string;
    setSortId: (value: string) => void;
    sortAsc: boolean;
    setSortAsc: (value: boolean) => void;
} {
    // NB: The atom is not reactive for the case when the user has opened this in multiple tabs - so keep a local copy of the state
    const { get, set } = useStore();

    const item = getOwn(get(sortAtom), listId);
    const [sortId, setSortId] = useState(getValidSortId(sortFields, item?.fieldId));
    const [sortAsc, setSortAsc] = useState(item?.asc ?? true);

    return {
        sortId,
        setSortId(value: string) {
            setSortId(value);
            set(sortAtom, (draft) => {
                (draft[listId] ??= {}).fieldId = value;
            });
        },

        sortAsc,
        setSortAsc(value: boolean) {
            setSortAsc(value);
            set(sortAtom, (draft) => {
                (draft[listId] ??= {}).asc = value;
            });
        },
    };
}
